import { Timestamp } from 'firebase/firestore';
import { Event } from '../types/Event';

/**
 * Converts any timestamp-like value to a Firebase Firestore Timestamp.
 * Handles Firestore Timestamps, JavaScript Date objects, ISO strings, 
 * timestamp objects with seconds/nanoseconds, and more.
 * 
 * @param value - The timestamp-like value to convert
 * @param defaultValue - Optional default value to return if conversion fails (default: null)
 * @returns Firestore Timestamp or defaultValue if conversion fails
 */
export const toFirestoreTimestamp = (
  value: any, 
  defaultValue: Timestamp | null = null
): Timestamp | null => {
  // Handle null/undefined
  if (value === null || value === undefined) {
    console.log('toFirestoreTimestamp: null or undefined value');
    return defaultValue;
  }

  try {
    // Already a Firestore Timestamp
    if (value instanceof Timestamp || (value && typeof value.toDate === 'function')) {
      return value as Timestamp;
    }

    // JavaScript Date object
    if (value instanceof Date) {
      return Timestamp.fromDate(value);
    }

    // String timestamp (ISO format, etc.)
    if (typeof value === 'string') {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return Timestamp.fromDate(date);
      }
    }

    // Number (milliseconds since epoch)
    if (typeof value === 'number') {
      return Timestamp.fromMillis(value);
    }

    // Object with seconds/nanoseconds (like Firestore timestamp data)
    if (typeof value === 'object' && 'seconds' in value) {
      if (typeof value.seconds === 'number') {
        const nanoseconds = value.nanoseconds || 0;
        return new Timestamp(value.seconds, nanoseconds);
      }
    }

    // Unix timestamp in seconds (convert to milliseconds)
    if (typeof value === 'number') {
      // If value is likely seconds not milliseconds (before year 2100)
      if (value < 4102444800) {
        return Timestamp.fromMillis(value * 1000);
      }
      return Timestamp.fromMillis(value);
    }

    console.log('toFirestoreTimestamp: Failed to convert value:', value);
    return defaultValue;
  } catch (error) {
    console.error('toFirestoreTimestamp: Error converting value:', error);
    return defaultValue;
  }
};

/**
 * Converts an array of timestamp-like values to Firestore Timestamp objects.
 * Handles mixed arrays with different timestamp formats.
 * 
 * @param values - Array of timestamp-like values
 * @returns Array of Firestore Timestamps (null for values that couldn't be converted)
 */
export const toFirestoreTimestampArray = (
  values: any[]
): (Timestamp | null)[] => {
  if (!Array.isArray(values)) {
    console.log('toFirestoreTimestampArray: Input is not an array:', values);
    return [];
  }

  return values.map(value => toFirestoreTimestamp(value));
};

/**
 * Checks if a value is a valid timestamp that can be converted to a Firestore Timestamp.
 * 
 * @param value - Value to check
 * @returns Boolean indicating if the value can be converted to a Firestore Timestamp
 */
export const isValidTimestamp = (value: any): boolean => {
  return toFirestoreTimestamp(value) !== null;
};

/**
 * Adds hours to a timestamp and returns a new Firestore Timestamp.
 * 
 * @param timestamp - Any timestamp-like value
 * @param hours - Number of hours to add (can be negative)
 * @returns New Firestore Timestamp with added hours, or null if conversion fails
 */
export const addHoursToTimestamp = (
  timestamp: any,
  hours: number
): Timestamp | null => {
  const firestoreTimestamp = toFirestoreTimestamp(timestamp);
  if (!firestoreTimestamp) return null;
  
  const date = firestoreTimestamp.toDate();
  date.setHours(date.getHours() + hours);
  return Timestamp.fromDate(date);
};

/**
 * Formats a timestamp-like value to a readable string.
 * 
 * @param timestamp - Any timestamp-like value
 * @param format - Optional format string ('date', 'time', 'datetime', or 'iso')
 * @returns Formatted string or empty string if conversion fails
 */
export const formatTimestamp = (
  timestamp: any,
  format: 'date' | 'time' | 'datetime' | 'iso' = 'datetime'
): string => {
  const firestoreTimestamp = toFirestoreTimestamp(timestamp);
  if (!firestoreTimestamp) return '';
  
  const date = firestoreTimestamp.toDate();
  
  switch (format) {
    case 'date':
      return date.toLocaleDateString();
    case 'time':
      return date.toLocaleTimeString();
    case 'datetime':
      return date.toLocaleString();
    case 'iso':
      return date.toISOString();
    default:
      return date.toLocaleString();
  }
};

/**
 * Standardizes all timestamp fields in an event object to ensure they are valid Firestore Timestamps.
 * This handles both single timestamps and arrays of timestamps.
 * 
 * @param event - The event object to standardize
 * @returns A new event object with standardized timestamps
 */
export const standardizeEventTimestamps = (event: Event): Event => {
  if (!event) return event;

  const standardizedEvent = { ...event };

  // Handle startDateTime (could be array or single value)
  if (Array.isArray(event.startDateTime)) {
    // Filter out null values after conversion to ensure Timestamp[] type
    const convertedArray = event.startDateTime.map(ts => toFirestoreTimestamp(ts))
      .filter((ts): ts is Timestamp => ts !== null);
    standardizedEvent.startDateTime = convertedArray.length > 0 ? convertedArray : [Timestamp.now()];
  } else if (event.startDateTime !== undefined) {
    // Use Timestamp.now() as a fallback if conversion returns null
    standardizedEvent.startDateTime = toFirestoreTimestamp(event.startDateTime) || Timestamp.now();
  }

  // Handle endDateTime (could be array or single value)
  if (Array.isArray(event.endDateTime)) {
    // For endDateTime we can have null values, but we need to handle the type correctly
    const convertedArray = event.endDateTime.map(ts => 
      ts ? toFirestoreTimestamp(ts) : null
    ).filter((ts): ts is Timestamp => ts !== null);
    
    // Only assign if we have values
    if (convertedArray.length > 0) {
      standardizedEvent.endDateTime = convertedArray;
    } else {
      // Keep as is if we can't convert any values
      standardizedEvent.endDateTime = event.endDateTime;
    }
  } else if (event.endDateTime !== undefined) {
    const converted = toFirestoreTimestamp(event.endDateTime);
    if (converted) {
      standardizedEvent.endDateTime = converted;
    }
  }

  // Handle createdAt (string or Timestamp)
  if (event.createdAt) {
    const converted = toFirestoreTimestamp(event.createdAt);
    if (converted) {
      standardizedEvent.createdAt = converted;
    }
  }

  // We'll skip updatedAt and publishedAt since they don't exist on the Event type

  return standardizedEvent;
};

/**
 * Bulk standardizes timestamps for an array of events.
 * 
 * @param events - Array of events to standardize
 * @returns Array of events with standardized timestamps
 */
export const standardizeEventsTimestamps = (events: Event[]): Event[] => {
  if (!Array.isArray(events)) return [];
  return events.map(standardizeEventTimestamps);
}; 